import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '@/features/auth/AuthProvider';

/** Full-page spinner shown while the boot-time session restore is in flight. */
function FullPageSpinner() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

/**
 * Requires a signed-in user.
 *
 * Waits for the boot refresh to settle before deciding. Without that wait, every
 * reload would bounce to the login screen for a frame and then bounce back —
 * because at first render the in-memory token is always empty.
 */
export function RequireAuth() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (!isAuthenticated) {
    // `state` carries where they were headed, so sign-in returns them there
    // instead of dumping everyone on the dashboard.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // An administrator-set password is a shared secret until it is changed. The
  // user is held on the change-password screen until it is.
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <Outlet />;
}

/** Sends an already-signed-in user away from the login screen. */
export function RequireAnonymous() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <FullPageSpinner />;
  }

  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Outlet />;
}

/**
 * Requires a permission for a whole route.
 *
 * A convenience, not a security control — the server re-checks every request.
 * Its real value is that a user without access sees an honest "no permission"
 * page instead of a screen that renders and then fails with 403s everywhere.
 */
export function RequirePermission({ permission }: { permission: string }) {
  const { can } = useAuth();
  return can(permission) ? <Outlet /> : <Navigate to="/403" replace />;
}

/**
 * Renders children only when the user holds a permission.
 *
 * For hiding buttons the user cannot use. Hiding a control is a courtesy; the
 * endpoint behind it is what actually enforces access.
 */
export function PermissionGate({
  permission,
  children,
  fallback = null,
}: {
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <>{fallback}</>;
}
